import { forwardRef, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HugeiconsIcon } from '@hugeicons/react'
import { ArrowUpRight01Icon, Comment, LinkSquare01Icon } from '@hugeicons/core-free-icons'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import ExternalContentInteractions from '@/components/ExternalContentInteractions'
import ExternalLink from '@/components/ExternalLink'
import WebPreview from '@/components/WebPreview'
import PostEditor from '@/components/PostEditor'
import SecondaryPageLayout from '@/layouts/SecondaryPageLayout'
import NotFoundPage from './NotFoundPage'
import { normalizeHttpUrlPublic, simplifyUrl } from '@/lib/url'
import { canWriteAtom } from '@/atoms/session'
import { useAtomValue } from 'jotai'

const ExternalContentPage = forwardRef(
  ({ url: rawUrl, index }: { url?: string; index?: number }, ref) => {
    const { t } = useTranslation()

    const url = useMemo(() => (rawUrl ? normalizeHttpUrlPublic(rawUrl) : ''), [rawUrl])

    const [isPostEditorOpen, setIsPostEditorOpen] = useState(false)
    const canWrite = useAtomValue(canWriteAtom)

    useEffect(() => {
      if (!url) return
      document.title = t('`{{url}}` on {{appName}}', {
        url: simplifyUrl(url),
        appName: window.hallway.universe.appName
      })
      history.replaceState(null, '')
    }, [url])

    if (!url) return <NotFoundPage ref={ref} />

    return (
      <SecondaryPageLayout
        ref={ref}
        index={index}
        title={simplifyUrl(url)}
        displayScrollToTopButton
        controls={
          <Button
            variant="ghost"
            size="titlebar-icon"
            onClick={() => window.open(url, '_blank', 'noreferrer')}
            title={t('Open in browser')}
          >
            <HugeiconsIcon icon={ArrowUpRight01Icon} />
          </Button>
        }
      >
        <div className="px-4 pt-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <HugeiconsIcon icon={LinkSquare01Icon} className="size-4 shrink-0" />
            <ExternalLink url={url} className="font-semibold break-all" />
          </div>
          <div className="mt-3">
            <WebPreview url={url} />
          </div>
        </div>
        <div className="px-4 pt-3">
          <Button
            variant="secondary"
            className="w-full"
            disabled={!canWrite}
            onClick={() => setIsPostEditorOpen(true)}
          >
            <HugeiconsIcon icon={Comment} className="mr-1" />
            {t('Comment')}
          </Button>
        </div>
        <PostEditor open={isPostEditorOpen} setOpen={setIsPostEditorOpen} parentExternal={url} />
        <Separator className="mt-4" />
        <ExternalContentInteractions pageIndex={index} url={url} />
      </SecondaryPageLayout>
    )
  }
)

ExternalContentPage.displayName = 'ExternalContentPage'
export default ExternalContentPage
